'use client'

import { ExclamationCircleFilled32 } from '@vinted/monochrome-icons'
import { Button, Card, Cell, Icon, Spacer, Text } from '@vinted/web-ui'
import { useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { PhotoTips } from '@marketplace-web/item-upload/photo-tips-feature'
import {
  getPhotoTip,
  transformPhotoTipResponse,
} from '@marketplace-web/item-upload/photo-tips-data'
import { ItemPageAlertsPluginModel } from '@marketplace-web/item-page/item-page-data'
import { ItemAlertStatus } from '@marketplace-web/item-page/item-status-data'

import { EDIT_ITEM_URL_WITH_ANCHOR } from '../../constants/routes'

type CtaButtonProps = Partial<React.ComponentProps<typeof Button>> | null

const ItemPageAlertsPlugin = ({ itemId, itemAlert }: ItemPageAlertsPluginModel) => {
  const [isPhotoTipsOpen, setIsPhotoTipsOpen] = useState(false)
  const translate = useTranslate('item.alerts')
  const { fetch: fetchPhotoTip, transformedData: photoTip } = useFetch(
    getPhotoTip,
    transformPhotoTipResponse,
  )

  const closePhotoTips = () => setIsPhotoTipsOpen(false)

  const openPhotoTips = () => {
    if (!itemAlert.photoTipId) return

    setIsPhotoTipsOpen(true)

    if (photoTip) return

    fetchPhotoTip(itemAlert.photoTipId)
  }

  const getCtaButtonProps = (): CtaButtonProps => {
    if (itemAlert.photoTipId)
      return {
        text: translate('actions.learn_more'),
        onClick: openPhotoTips,
      }

    switch (itemAlert.itemAlertType) {
      case ItemAlertStatus.PackageSize: {
        return {
          text: translate('actions.package_size'),
          url: EDIT_ITEM_URL_WITH_ANCHOR(itemId, 'packages-selector'),
        }
      }
      case ItemAlertStatus.MissingSubcategory: {
        return {
          text: translate('actions.missing_subcategory'),
          url: EDIT_ITEM_URL_WITH_ANCHOR(itemId, 'categories'),
        }
      }
      default:
        return null
    }
  }

  const renderCtaButton = () => {
    const ctaButtonProps = getCtaButtonProps()

    if (!ctaButtonProps) return null

    return (
      <div className="u-flexbox u-align-items-center">
        <Spacer orientation="vertical" />
        <Button {...ctaButtonProps} styling="filled" size="medium" testId="item-alert-cta-button" />
      </div>
    )
  }

  const renderDescription = () => {
    if (!itemAlert.detailedDescription) return null

    return (
      <>
        <Card>
          <Cell suffix={renderCtaButton()} testId="item-alert-box">
            <div className="u-flexbox u-align-items-center">
              <Icon name={ExclamationCircleFilled32} color="warning-default" />
              <Spacer orientation="vertical" size="large" />
              <Text
                as="h3"
                text={itemAlert.detailedDescription}
                type="subtitle"
                theme="amplified"
              />
            </div>
          </Cell>
        </Card>
        <Spacer />
      </>
    )
  }

  return (
    <>
      {renderDescription()}
      {itemAlert.photoTipId && photoTip && (
        <PhotoTips isOpen={isPhotoTipsOpen} photoTips={[photoTip]} handleClose={closePhotoTips} />
      )}
    </>
  )
}

export default ItemPageAlertsPlugin
